import { BufferGeometry, Float32BufferAttribute } from "three"

type InwardPointedPlaneGeometryParameters = {
  width: number,
  height: number,
  widthSegments: number,
  heightSegments: number,
}

// https://chatgpt.com/share/67f997cb-aca0-8003-92e0-7e98143a92a2
/**
 * A geometry class for representing a plane
 * where the defining distiction from PlaneGeometry
 * is that the diagonal lines all point inwards.
 *
 * ```js
 * const geometry = new THREE.InwardPointedPlaneGeometry( 1, 1, 2, 2);
 * const material = new THREE.MeshBasicMaterial( { color: 0xffff00, side: THREE.DoubleSide } );
 * const plane = new THREE.Mesh( geometry, material );
 * scene.add( plane );
 * ```
 *
 * @augments BufferGeometry
 */
export default class InwardPointedPlaneGeometry extends BufferGeometry {

  type: string

  parameters: InwardPointedPlaneGeometryParameters

  /**
   * Constructs a new plane geometry.
   *
   * @param {number} [width=1] - The width along the X axis.
   * @param {number} [height=1] - The height along the Y axis
   * @param {number} [widthSegments=1] - The number of segments along the X axis.
   * @param {number} [heightSegments=1] - The number of segments along the Y axis.
   */
  constructor( width = 1, height = 1, widthSegments = 1, heightSegments = 1 ) {

    super();

    this.type = 'InwardPointedPlaneGeometry';

    /**
     * Holds the constructor parameters that have been
     * used to generate the geometry. Any modification
     * after instantiation does not change the geometry.
     *
     * @type {Object}
     */
    this.parameters = {
      width: width,
      height: height,
      widthSegments: widthSegments,
      heightSegments: heightSegments
    };

    const width_half = width / 2
    const height_half = height / 2

    const gridX = Math.floor(widthSegments)
    const gridY = Math.floor(heightSegments)

    const gridX1 = gridX + 1
    const gridY1 = gridY + 1

    const segment_width = width / gridX
    const segment_height = height / gridY

    const indices: number[] = []
    const vertices: number[] = []
    const normals: number[] = []
    const uvs: number[] = []

    // Generate vertices, normals, uvs
    for (let iy = 0; iy < gridY1; iy++) {
      const y = iy * segment_height - height_half

      for (let ix = 0; ix < gridX1; ix++) {
        const x = ix * segment_width - width_half

        vertices.push(x, -y, 0)
        normals.push(0, 0, 1)
        uvs.push(ix / gridX, 1 - iy / gridY)
      }
    }

    // Generate indices, with inward diagonals
    const centerX = gridX / 2
    const centerY = gridY / 2

    for (let iy = 0; iy < gridY; iy++) {
      for (let ix = 0; ix < gridX; ix++) {
        const a = ix + gridX1 * iy
        const b = ix + gridX1 * (iy + 1)
        const c = (ix + 1) + gridX1 * (iy + 1)
        const d = (ix + 1) + gridX1 * iy

        const dx = ix + 0.5 - centerX
        const dy = iy + 0.5 - centerY

        const isTopLeft = (dx < 0 && dy < 0)
        // const isTopRight = (dx >= 0 && dy < 0)
        // const isBottomLeft = (dx < 0 && dy >= 0)
        const isBottomRight = (dx >= 0 && dy >= 0)

        // Alternate triangulation per quadrant to "point inward"
        if (isTopLeft || isBottomRight) {
          // Diagonal: a -> c
          indices.push(a, b, c)
          indices.push(a, c, d)
        } else {
          // Diagonal: b -> d
          indices.push(a, b, d)
          indices.push(b, c, d)
        }
      }
    }

    this.setIndex(indices)
    this.setAttribute('position', new Float32BufferAttribute(vertices, 3))
    this.setAttribute('normal', new Float32BufferAttribute(normals, 3))
    this.setAttribute('uv', new Float32BufferAttribute(uvs, 2))
  }

  copy( source : InwardPointedPlaneGeometry ) {

    super.copy( source );

    this.parameters = Object.assign( {}, source.parameters );

    return this;

  }

  /**
   * Factory method for creating an instance of this class from the given
   * JSON object.
   *
   * @param {Object} data - A JSON object representing the serialized geometry.
   * @return {InwardPointedPlaneGeometry} A new instance.
   */
  static fromJSON( data : InwardPointedPlaneGeometryParameters ) {

    return new InwardPointedPlaneGeometry( data.width, data.height, data.widthSegments, data.heightSegments );

  }

}
